import React, { useEffect, useState } from 'react';
import {
  Typography, Select, Button, Input, Space, message, Card, Tag, Spin, Divider,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  fetchUsers,
  fetchPermissionSets,
  fetchUserById,
  assignPermissionsToUser,
  fetchEffectivePermissions,
} from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

// Attach reusable group/role sets (and an optional user override) to a user, then
// preview the merged effective permissions resolved by the backend.
const AssignPermissionsPage = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [groups, setGroups] = useState([]); // scope role|group sets
  const [overrides, setOverrides] = useState([]); // scope user sets

  const [groupIds, setGroupIds] = useState([]);
  const [overrideId, setOverrideId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [effective, setEffective] = useState([]);
  const [loadingEffective, setLoadingEffective] = useState(false);

  // Load assignable sets once.
  useEffect(() => {
    (async () => {
      try {
        const [roleRes, groupRes, userRes] = await Promise.all([
          fetchPermissionSets({ scope: 'role', limit: 100 }),
          fetchPermissionSets({ scope: 'group', limit: 100 }),
          fetchPermissionSets({ scope: 'user', limit: 100 }),
        ]);
        setGroups([...(roleRes.data || []), ...(groupRes.data || [])]);
        setOverrides(userRes.data || []);
      } catch (err) {
        console.error(err);
        messageApi.error('Failed to load permission sets');
      }
    })();
  }, [messageApi]);

  const handleUserSearch = async () => {
    setLoadingUsers(true);
    try {
      const result = await fetchUsers({ search: userSearch });
      setUserOptions(result.data || []);
    } catch (err) {
      messageApi.error('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadEffective = async (userId) => {
    setLoadingEffective(true);
    try {
      const res = await fetchEffectivePermissions(userId);
      setEffective(res.permissions || []);
    } catch (err) {
      setEffective([]);
    } finally {
      setLoadingEffective(false);
    }
  };

  const handleUserSelect = async (userId) => {
    setSelectedUserId(userId);
    try {
      const user = await fetchUserById(userId);
      const u = user?.data || user;
      setGroupIds((u?.permissionGroups || []).map((g) => (typeof g === 'object' ? g._id : g)));
      setOverrideId(u?.permissionOverride
        ? (typeof u.permissionOverride === 'object' ? u.permissionOverride._id : u.permissionOverride)
        : null);
    } catch {
      setGroupIds([]);
      setOverrideId(null);
    }
    loadEffective(userId);
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      messageApi.warning('Select a user first');
      return;
    }
    setSaving(true);
    try {
      await assignPermissionsToUser({
        userId: selectedUserId,
        groupIds,
        overrideId: overrideId || null,
      });
      messageApi.success('Assignment saved');
      loadEffective(selectedUserId);
    } catch (err) {
      messageApi.error('Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ padding: 24 }}>
        <Title level={3}>Assign Permissions to User</Title>

        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search by email or phone"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            style={{ width: 300 }}
            onPressEnter={handleUserSearch}
          />
          <Button icon={<SearchOutlined />} onClick={handleUserSearch} loading={loadingUsers}>
            Search
          </Button>
          <Select
            placeholder="Select user"
            value={selectedUserId}
            onChange={handleUserSelect}
            style={{ width: 320 }}
            filterOption={false}
          >
            {userOptions.map((user) => (
              <Option key={user._id} value={user._id}>
                {user.name} ({user.email || user.phone}) — {user.role}
              </Option>
            ))}
          </Select>
        </Space>

        {selectedUserId && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card title="Groups & Roles (merged by priority)" size="small">
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Assign role/group sets"
                value={groupIds}
                onChange={setGroupIds}
                optionFilterProp="label"
              >
                {groups.map((g) => (
                  <Option key={g._id} value={g._id} label={g.name}>
                    [{g.scope}] {g.name}{g.key ? ` (${g.key})` : ''} · p{g.priority || 0}
                  </Option>
                ))}
              </Select>
            </Card>

            <Card title="User Override (highest precedence, optional)" size="small">
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Optional user-specific override set"
                value={overrideId || undefined}
                onChange={(v) => setOverrideId(v || null)}
                optionFilterProp="label"
              >
                {overrides.map((o) => (
                  <Option key={o._id} value={o._id} label={o.name}>
                    {o.name}
                  </Option>
                ))}
              </Select>
            </Card>

            <Button type="primary" loading={saving} onClick={handleSave}>
              Save Assignment
            </Button>

            <Divider />

            <Card title="Effective Permissions (resolved)" size="small">
              {loadingEffective ? (
                <Spin />
              ) : effective.length === 0 ? (
                <Text type="secondary">No permissions resolved.</Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {effective.map((p) => (
                    <div key={p.module}>
                      <Text strong>{p.module}</Text>{' '}
                      {(p.topLevel || []).map((a) => (
                        <Tag color="blue" key={a}>{a}</Tag>
                      ))}
                      {(p.viewableFields || []).length > 0 && (
                        <Text type="secondary"> · view: {p.viewableFields.join(', ')}</Text>
                      )}
                      {(p.editableFields || []).length > 0 && (
                        <Text type="secondary"> · edit: {p.editableFields.join(', ')}</Text>
                      )}
                    </div>
                  ))}
                </Space>
              )}
            </Card>
          </Space>
        )}
      </div>
    </>
  );
};

export default AssignPermissionsPage;
